import { Box, CardMedia, Grid, Paper, Theme, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import React from "react";
import { useHistory } from "react-router-dom";
import ActiveIcon from "../common/assets/images/iconStatusActive.svg";
import InactiveIcon from "../common/assets/images/iconStatusInactive.svg";
import OtherIcon from "../common/assets/images/iconStatusOther.svg";
import { fetchData } from "../common/services/ApiClient";
import { formatDate, setInstitutionStatus } from "../common/services/Utilities";
import { ApiBaseUrl, AppUrl } from "../config";
import {
  INSTITUTION_STATUS,
  ResultRecord,
  SucceedingInstitution,
} from "../interfaces";
import RouterLink from "./RouterLink";
import { CardListCondensedItem } from "./SearchResultCard/SearchResultCard";

const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    margin: "5px auto",
  },
  textAlign: {
    "textAlign": "center",
    "& .MuiGrid-item": {
      width: "100%",
    },
  },
  arrowContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  arrow: {
    borderLeft: "55px solid transparent",
    borderRight: "55px solid transparent",
    borderTop: "40px solid #E9EDF2",
  },
  arrowTail: {
    width: "50px",
    height: "15px",
    backgroundColor: "#E9EDF2",
  },
  succeedingInstButton: {
    "border": "1px solid #D9E7F6",
    "boxShadow": theme.shadows[2],
    "height": "auto",
    "textAlign": "center",
    "cursor": "pointer",
    "transition": ".3s ease",
    "marginBottom": "1rem",
    "marginTop": 0,
    "width": "100%",
    "display": "block",
    "&#succeeding-institution:hover, &#succeeding-institution:focus": {
      boxShadow: theme.shadows[5],
      textDecoration: "none",
    },
  },
}));

interface SucceedingInstDisplayProps {
  succeedingInst: SucceedingInstitution;
  mostRecentStatus: any;
  iconFontSize: number;
}

const SucceedingInstDisplay = ({
  succeedingInst,
  mostRecentStatus,
  iconFontSize,
}: SucceedingInstDisplayProps) => {
  if (succeedingInst) {
    const instToDisplay = succeedingInst.resultData[0];

    return (
      <Box marginTop={1}>
        <Typography
          color="primary"
          fontWeight="bold"
          id="succeeding-institution"
        >
          {instToDisplay.NAME}
        </Typography>
        <Typography color="primary" fontWeight="bold">
          Cert - {instToDisplay.CERT}
        </Typography>
        <Box fontSize={iconFontSize}>
          <CardMedia
            style={{ width: iconFontSize, color: mostRecentStatus.color }}
            className={mostRecentStatus.class}
            component="img"
            alt={mostRecentStatus.altText}
            src={mostRecentStatus.component}
          />
        </Box>
        <Typography color="primary" fontWeight="bold">
          {mostRecentStatus.altText}
        </Typography>
        <Typography>Since {formatDate(instToDisplay.INSDATE)}</Typography>
        <Typography gutterBottom color="primary" fontWeight="bold" marginY={1}>
          Click to View Succeeding Institution
        </Typography>
      </Box>
    );
  } else {
    return <></>;
  }
};

export default function ActiveStatus(props: {
  institution: ResultRecord;
  levelTwo?: boolean;
}) {
  const history = useHistory();
  const classes = useStyles();
  const { institution, levelTwo } = props;
  const [succeedingInst, setSucceedingInst] = React.useState<
    SucceedingInstitution
  >({
    resultData: [],
    display: false,
    primaryRegulator: "",
  });

  if (
    succeedingInst.display &&
    history.location.state &&
    history.location.state.from === "succeedingInstitutions"
  ) {
    setSucceedingInst({ ...succeedingInst, display: false });
  }

  const iconFontSize = levelTwo ? 72 : 64;
  const textFontSize = 16;

  function getInstitutionStatusDescriptions(institution: ResultRecord) {
    const active = {
      component: ActiveIcon,
      altText: "FDIC Insured",
      color: "#0ca900",
      class: clsx("activeIcon", classes.icon),
      mainDescription: "FDIC Insured",
      shortDescription: [
        institution.INSDATE ? `Since ${formatDate(institution.INSDATE)}` : "",
      ],
      longDescription: [
        institution.INSDATE ? `Since ${formatDate(institution.INSDATE)}` : "",
      ],
    };
    const inactive = {
      component: InactiveIcon,
      altText: "Inactive Institution",
      color: "#f59a23",
      class: clsx("inactiveIcon", classes.icon),
      mainDescription: "Institution Closed",
      shortDescription: institution.ENDEFYMD
        ? [`as of ${formatDate(institution.ENDEFYMD)}`, "Insured until closed"]
        : [""],
      longDescription: institution.ENDEFYMD
        ? [`as of ${formatDate(institution.ENDEFYMD)}`, "Insured until closed"]
        : [""],
    };
    const statusLookup: { [key in INSTITUTION_STATUS]: any } = {
      OPEN: {
        component: ActiveIcon,
        altText: "FDIC Insured",
        color: "#0ca900",
        class: clsx("activeIcon", classes.icon),
        mainDescription: "FDIC Insured",
        shortDescription: [`Since ${formatDate(institution.INSDATE)}`],
        longDescription: [`Since ${formatDate(institution.INSDATE)}`],
      },
      CLOSED_WITH_GOV: {
        component: InactiveIcon,
        altText: "Inactive Institution",
        color: "#f59a23",
        class: clsx("inactiveIcon", classes.icon),
        mainDescription: "Received Government Assistance",
        shortDescription: [
          `on ${formatDate(institution.ENDEFYMD)}`,
          "Insured until closed",
        ],
        longDescription: [
          `Failed on ${formatDate(institution.ENDEFYMD)}`,
          "and was insured until closed",
        ],
      },
      CLOSED_WITH_GOV_MERGED: {
        component: InactiveIcon,
        altText: "Inactive Institution",
        color: "#f59a23",
        class: clsx("inactiveIcon", classes.icon),
        mainDescription: "Received Government Assistance",
        shortDescription: [
          `on ${formatDate(institution.ENDEFYMD)}`,
          "Insured until closed",
        ],
        longDescription: [
          `Failed on ${formatDate(institution.ENDEFYMD)}`,
          "and was insured until acquired",
        ],
      },
      CLOSED_WITHOUT_GOV_ACQUIRED_MERGE: {
        component: InactiveIcon,
        altText: "Inactive Institution",
        color: "#f59a23",
        class: clsx("inactiveIcon", classes.icon),
        mainDescription: "Institution Closed",
        shortDescription: [
          `as of ${formatDate(institution.ENDEFYMD)}`,
          "Insured until closed",
        ],
        longDescription: [
          `Merged or acquired on ${formatDate(institution.ENDEFYMD)}`,
          "without government assistance",
        ],
      },
      CLOSED_WITHOUT_GOV: {
        component: InactiveIcon,
        altText: "Inactive Institution",
        color: "#f59a23",
        class: clsx("inactiveIcon", classes.icon),
        mainDescription: "Institution Closed",
        shortDescription: [
          `as of ${formatDate(institution.ENDEFYMD)}`,
          "Insured until closed",
        ],
        longDescription: [
          `Closed on ${formatDate(institution.ENDEFYMD)} without`,
          "government assistance",
        ],
      },
      RELINQUISHED: {
        component: OtherIcon,
        altText: "No Longer FDIC Insured",
        color: "#d7d7d7",
        class: clsx("otherIcon", classes.icon),
        mainDescription: "Insurance Relinquished",
        shortDescription: [`as of ${formatDate(institution.INSDROPDATE)}`],
        longDescription: [
          "Voluntarily relinquished FDIC insurance.",
          `Insured until ${formatDate(institution.INSDROPDATE)}`,
        ],
      },
    };

    return institution.INSTITUTION_STATUS
      ? statusLookup[institution.INSTITUTION_STATUS]
      : institution.ACTIVE === 1
      ? active
      : inactive;
  }

  const status = getInstitutionStatusDescriptions(institution);

  React.useEffect(() => {
    if (
      institution?.ULTCERT &&
      levelTwo &&
      institution.ULTCERT.toString() !== institution.CERT.toString()
    ) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/institutions?filters=CERT:${institution.ULTCERT}`,
        )
          .then(response => {
            if (response.data.length > 0 && isMounted) {
              setSucceedingInst({
                resultData: response.data.map((data: any) =>
                  setInstitutionStatus(data),
                ),
                display: true,
                primaryRegulator: response.data[0].REGAGNT,
              });
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [institution, levelTwo]);

  return levelTwo && succeedingInst.display ? (
    <RouterLink
      buttonLink
      id="succeeding-institution"
      className={classes.succeedingInstButton}
      href={`${AppUrl("BankFind")}/details/${
        succeedingInst.resultData[0].CERT
      }`}
      from="succeedingInstitutions"
    >
      <Paper sx={{ padding: 2 }}>
        <Grid container className={classes.textAlign}>
          <Grid item>
            <Box fontSize={iconFontSize}>
              <CardMedia
                style={{ width: iconFontSize, color: status.color }}
                className={status.class}
                component="img"
                alt={status.altText}
                src={status.component}
              />
            </Box>
            <Typography color="primary" component="div">
              <Box fontWeight="fontWeightBold" fontSize={textFontSize}>
                <Box className="statusText">
                  <Box fontWeight="inherit" fontSize="inherited">
                    <CardListCondensedItem
                      className={classes.textAlign}
                      itemLabel={status.mainDescription}
                      itemValue={
                        props.levelTwo
                          ? status.longDescription[0]
                          : status.shortDescription[0]
                      }
                    />
                    {status.shortDescription[1] || status.longDescription[1] ? (
                      <CardListCondensedItem
                        className={classes.textAlign}
                        itemValueId="active-status-sub-description"
                        itemValue={
                          props.levelTwo
                            ? status.longDescription[1]
                            : status.shortDescription[1]
                        }
                      />
                    ) : (
                      undefined
                    )}
                  </Box>
                  {succeedingInst && (
                    <>
                      <Box marginTop={3}>
                        <Typography color="primary" marginBottom={-1}>
                          See the succeeding institution
                        </Typography>
                        <Typography color="primary">
                          for more information.
                        </Typography>
                        <Box className={classes.arrowContainer}>
                          <Box className={classes.arrowTail} />
                          <Box className={classes.arrow} />
                        </Box>
                      </Box>
                      <SucceedingInstDisplay
                        succeedingInst={succeedingInst}
                        mostRecentStatus={getInstitutionStatusDescriptions(
                          succeedingInst.resultData[0],
                        )}
                        iconFontSize={iconFontSize}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </RouterLink>
  ) : (
    <Grid container className={classes.textAlign}>
      <Grid item>
        <Box fontSize={iconFontSize}>
          <CardMedia
            style={{ width: iconFontSize, color: status.color }}
            className={status.class}
            component="img"
            alt={status.altText}
            src={status.component}
          />
        </Box>
        <Typography color="primary" component="div">
          <Box fontWeight="fontWeightBold" fontSize={textFontSize}>
            <Box className="statusText">
              <Box fontWeight="inherit" fontSize="inherited">
                <CardListCondensedItem
                  className={classes.textAlign}
                  itemLabel={status.mainDescription}
                  itemValue={
                    props.levelTwo
                      ? status.longDescription[0]
                      : status.shortDescription[0]
                  }
                />
                {status.shortDescription[1] || status.longDescription[1] ? (
                  <CardListCondensedItem
                    className={classes.textAlign}
                    itemValueId="active-status-sub-description"
                    itemValue={
                      props.levelTwo
                        ? status.longDescription[1]
                        : status.shortDescription[1]
                    }
                  />
                ) : (
                  undefined
                )}
              </Box>
            </Box>
          </Box>
        </Typography>
      </Grid>
    </Grid>
  );
}
